import { useState } from "react";
import Button from "./ui/Button";

const Stars: React.FC<{ value: number; onPick?: (n: number) => void }> = ({ value, onPick }) => (
  <span className="text-xl tracking-wide text-primary-500">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        disabled={!onPick}
        onClick={() => onPick?.(n)}
        className={onPick ? "cursor-pointer transition-transform hover:scale-110" : "cursor-default"}
        aria-label={`${n} star${n === 1 ? "" : "s"}`}
      >
        {n <= value ? "★" : "☆"}
      </button>
    ))}
  </span>
);

/**
 * Post-event "How was it?" rating for a decided plan. Shows the prompt once the
 * start time has passed (and the viewer hasn't rated), otherwise the family average.
 */
const RatePlan: React.FC<{
  startsAt?: string | null;
  yourRating?: number | null;
  average?: number | null;
  count: number;
  onRate: (stars: number, note: string | null) => Promise<void>;
}> = ({ startsAt, yourRating, average, count, onRate }) => {
  const [stars, setStars] = useState(yourRating ?? 0);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  // Floating local time; missing => treat as already started (older decided plans).
  const started = !startsAt || new Date(startsAt).getTime() <= Date.now();
  const alreadyRated = (yourRating ?? 0) > 0;

  const submit = async () => {
    if (stars < 1) return;
    setBusy(true);
    try {
      await onRate(stars, note.trim() || null);
    } finally {
      setBusy(false);
    }
  };

  if (alreadyRated) {
    return (
      <div className="mt-4 rounded-2xl border border-primary-100 bg-white/70 p-3 text-sm text-ink/70">
        You rated <Stars value={yourRating!} />
        {average != null && count > 0 && <span className="ml-2 text-ink/45">family avg {average.toFixed(1)}★ ({count})</span>}
      </div>
    );
  }

  if (!started) {
    return average != null && count > 0 ? (
      <div className="mt-4 text-center text-sm text-ink/55">Family rating so far: {average.toFixed(1)}★ ({count})</div>
    ) : null;
  }

  return (
    <div className="mt-4 rounded-2xl border border-primary-100 bg-white/70 p-4 text-left">
      <p className="text-sm font-medium text-ink">How was it?</p>
      <div className="mt-1">
        <Stars value={stars} onPick={setStars} />
      </div>
      <input
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Add a note (optional)"
        className="mt-2 w-full rounded-xl border border-primary-200 bg-white/70 px-3.5 py-2 text-sm text-ink placeholder:text-ink/40 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
      />
      <Button className="mt-2" onClick={submit} disabled={busy || stars < 1}>
        {busy ? "…" : "Submit rating"}
      </Button>
      {average != null && count > 0 && <p className="mt-2 text-xs text-ink/45">Family avg so far: {average.toFixed(1)}★ ({count})</p>}
    </div>
  );
};

export default RatePlan;
